import { QuestionCircleOutlined } from '@ant-design/icons'
import { Alert, Breakpoint, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { SubjectAreaSummaryItem } from '../../../common/subjectTypes'
import { AsyncState } from '../../../redux/fetchAction'
import { numberSorter, stringSorter } from '../../utils/generalTableHelpers'
import { useTableLocale } from '../../utils/useTableLocale'
import { Money } from '../Money/Money'
import { SubjectDetailAreaSummaryTableSummary } from '../SubjectDetailAreaSummaryTableSummary/SubjectDetailAreaSummaryTableSummary'

export interface SubjectDetailAreaSummaryTableProps {
  dataSource: AsyncState<SubjectAreaSummaryItem[]>
}

const renderCell = (
  value: number,
  symbol?: string,
  decimalNumbers?: number
) => (
  <Money
    amount={value}
    decimalNumbers={decimalNumbers || 0}
    symbol={symbol || 'm²'}
    grayscale
    className={value === 0 ? 'text-gray-400' : ''}
  />
)

const tableId = 'subjectDetailSummary-table'
const scrollX = { x: 700 }

export const SubjectDetailAreaSummaryTable: FunctionComponent<SubjectDetailAreaSummaryTableProps> =
  props => {
    const locale = useTableLocale(props.dataSource.fetching, 6)

    const columns = useMemo<ColumnsType<SubjectAreaSummaryItem>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Způsob využití pozemku',
          dataIndex: 'name',
          sorter: stringSorter('name'),
        },
        {
          title: (
            <Tooltip title="Souhrnná cena za ohodnocené pozemky dle BPEJ (Bonitovaná půdně ekologická jednotka)">
              <div>Cena dle</div>
              <div>
                BPEJ <QuestionCircleOutlined className="text-gray-400" />
              </div>
            </Tooltip>
          ),
          dataIndex: 'priceBpej',
          className: 'w-32 !text-right',
          showSorterTooltip: false,
          sorter: numberSorter('priceBpej'),
          render: value => renderCell(value, 'Kč'),
        },
        {
          title: 'Výměra',
          children: [
            {
              title: 'm²',
              dataIndex: 'areaM',
              className: 'w-32 !text-right',
              sorter: numberSorter('areaM'),
              render: value => renderCell(value),
            },
            {
              title: 'ha',
              dataIndex: 'areaHa',
              className: 'w-24 !text-right',
              sorter: numberSorter('areaHa'),
              render: value => renderCell(value, 'ha', 2),
            },
          ],
        },
        {
          title: (
            <Tooltip title="LPIS (Land Parcel Identification System) registr evidence využití zemědělské půdy.">
              Pronajato dle LPIS{' '}
              <QuestionCircleOutlined className="text-gray-400" />
            </Tooltip>
          ),
          children: [
            {
              title: 'm²',
              dataIndex: 'lpisArea',
              className: 'w-32 !text-right',
              sorter: numberSorter('lpisArea'),
              render: value => renderCell(value),
            },
            {
              title: '%',
              dataIndex: 'lpisPercentage',
              className: 'w-24 !text-right',
              sorter: numberSorter('lpisPercentage'),
              render: value => renderCell(value, '%', 2),
            },
          ],
        },
      ],
      []
    )

    const summary = useCallback(
      () => (
        <SubjectDetailAreaSummaryTableSummary
          dataSource={props.dataSource.data}
        />
      ),
      [props.dataSource.data]
    )

    if (props.dataSource.error)
      return (
        <Alert
          message="Chyba při načítání statistik"
          description="Je nám líto, ale nepodařilo se najít detailní informace k požadovanému subjektu."
          type="error"
          showIcon
        />
      )

    return (
      <Table
        id={tableId}
        rowKey="id"
        size="small"
        className="w-full"
        scroll={scrollX}
        dataSource={props.dataSource.data || []}
        columns={columns}
        loading={props.dataSource.fetching}
        summary={summary}
        locale={locale}
        pagination={false}
      />
    )
  }
